import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Share2, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/handball";

/**
 * ShareDialog
 * Låter användaren välja mellan:
 *  - "session" (specifik match/träning) – kräver att en session är vald
 *  - "all" (all data för målvakten)
 * Skapar ett delnings-token via POST /api/share och visar delnings-URL.
 */
export default function ShareDialog({
  open,
  onOpenChange,
  goalkeeperId,
  goalkeeperName,
  sessionId,
  sessionName,
  tournamentId,
  tournamentName,
}) {
  const defaultScope = tournamentId ? "tournament" : sessionId ? "session" : "all";
  const [scope, setScope] = useState(defaultScope);
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl("");
      setCopied(false);
      setScope(tournamentId ? "tournament" : sessionId ? "session" : "all");
    }
  }, [open, sessionId, tournamentId]);

  const buildUrl = (token) => `${window.location.origin}/share/${token}`;

  const generateLink = async () => {
    setCreating(true);
    try {
      const payload = {};
      if (scope === "tournament" && tournamentId) {
        payload.tournament_id = tournamentId;
      } else {
        payload.goalkeeper_id = goalkeeperId;
        if (scope === "session" && sessionId) payload.session_id = sessionId;
      }
      const { data } = await api.post("/share", payload);
      const shareUrl = buildUrl(data.token);
      setUrl(shareUrl);
      toast.success("Delningslänk skapad");
    } catch (e) {
      toast.error("Kunde inte skapa delningslänk");
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Länk kopierad");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  const nativeShare = async () => {
    if (!url) return;
    const title = tournamentName
      ? `Målvaktsstatistik – ${tournamentName}`
      : `Målvaktsstatistik – ${goalkeeperName}`;
    const text =
      scope === "tournament"
        ? `Sammanlagd statistik från ${tournamentName}`
        : scope === "session"
        ? `Statistik från ${sessionName || "sessionen"} för ${goalkeeperName}`
        : `All målvaktsstatistik för ${goalkeeperName}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (err) {
        // User aborted native share sheet – no-op
        console.debug("[share] native share cancelled", err?.name);
      }
    } else {
      copyUrl();
    }
  };

  const downloadQr = () => {
    const svg = document.getElementById("share-qr-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const scale = 4; // upscale for print quality
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement("a");
        const safeName = (goalkeeperName || "malvakt")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-");
        link.download = `qr-${safeName}.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      }, "image/png");
    };
    img.src = svgUrl;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white max-w-lg"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            Dela statistik
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Skapa en läsbar länk som visar heatmap och nyckeltal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-3">
              Vad vill du dela?
            </div>
            <RadioGroup
              value={scope}
              onValueChange={setScope}
              className="grid grid-cols-1 gap-2"
              data-testid="share-scope-group"
            >
              {tournamentId && (
                <ScopeRow
                  value="tournament"
                  testid="share-scope-tournament"
                  selected={scope === "tournament"}
                  title={tournamentName || "Vald turnering"}
                  subtitle="Hela turneringen/serien + lista på matcher"
                />
              )}
              <ScopeRow
                value="session"
                testid="share-scope-session"
                disabled={!sessionId}
                selected={scope === "session"}
                title={sessionId ? sessionName || "Vald session" : "Ingen session vald"}
                subtitle="Endast denna match/träning"
              />
              <ScopeRow
                value="all"
                testid="share-scope-all"
                disabled={!goalkeeperId}
                selected={scope === "all"}
                title={goalkeeperId ? `All data för ${goalkeeperName || "målvakten"}` : "Ingen målvakt vald"}
                subtitle="Alla matcher och träningar sammanlagt"
              />
            </RadioGroup>
          </div>

          {url ? (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                Delningslänk
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="share-url-input"
                  readOnly
                  value={url}
                  className="bg-black border-[#3F3F46] text-white font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  data-testid="share-copy-btn"
                  onClick={copyUrl}
                  variant="outline"
                  className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white shrink-0"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>

              <div className="flex gap-4 items-start pt-2">
                <div
                  className="p-3 bg-white border border-[#3F3F46] shrink-0"
                  data-testid="share-qr-wrapper"
                >
                  <QRCodeSVG
                    id="share-qr-svg"
                    value={url}
                    size={160}
                    level="M"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#0A0A0A"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="font-display uppercase tracking-widest text-sm text-white">
                    QR-kod
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-relaxed">
                    Skanna med telefonens kamera för att öppna statistiken
                    direkt. Perfekt för att växla från surfplatta till mobil.
                  </div>
                  <Button
                    data-testid="share-qr-download-btn"
                    onClick={downloadQr}
                    variant="outline"
                    className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white h-9 text-xs"
                  >
                    <Download size={14} className="mr-2" />
                    Ladda ner PNG
                  </Button>
                </div>
              </div>

              <div className="text-[11px] text-zinc-500">
                Vem som helst med länken kan se en läsbar version av statistiken.
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {url ? (
            <>
              <Button
                data-testid="share-native-btn"
                onClick={nativeShare}
                variant="outline"
                className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white"
              >
                <Share2 size={16} className="mr-2" />
                Dela
              </Button>
              <Button
                data-testid="share-new-btn"
                onClick={() => setUrl("")}
                variant="outline"
                className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white"
              >
                Skapa ny länk
              </Button>
            </>
          ) : (
            <Button
              data-testid="share-generate-btn"
              onClick={generateLink}
              disabled={
                creating ||
                (scope === "tournament" && !tournamentId) ||
                (scope === "session" && (!sessionId || !goalkeeperId)) ||
                (scope === "all" && !goalkeeperId)
              }
              className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
            >
              Skapa länk
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeRow({ value, testid, title, subtitle, disabled, selected }) {
  return (
    <label
      className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
        disabled
          ? "border-[#27272A] opacity-40 cursor-not-allowed"
          : selected
          ? "border-[#34C759] bg-[#0f1a12]"
          : "border-[#27272A] hover:border-[#3F3F46]"
      }`}
      data-testid={testid}
    >
      <RadioGroupItem
        value={value}
        disabled={disabled}
        className="mt-1 border-[#3F3F46] text-[#34C759]"
      />
      <div>
        <div className="font-display uppercase tracking-widest text-sm text-white">
          {title}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</div>
      </div>
    </label>
  );
}
